import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import {
  COCKTAIL_STATUSES,
  INGREDIENT_CATEGORIES,
  TECHNIQUES,
  UNITS,
  convertAmount,
  estimateDrink,
  formatAmount,
  isVolumeUnit,
  normalizeTags,
  parseAmount,
  type AiDraft,
  type CocktailDetail,
  type CocktailStatus,
  type Ingredient,
  type Settings,
  type IngredientCategory,
  type Technique,
  type Unit,
  type VersionDetail,
} from '@muddle/core';
import { api, ApiError } from '../api';
import { useAsync } from '../hooks/useAsync';
import { estimateText } from '../format';
import { useTitle } from '../shortcuts';
import { ErrorMessage, PageHeader, Skeleton, useToast } from '../components';

interface Line {
  key: string;
  text: string;
  amount: string;
  unit: Unit;
  newCategory: IngredientCategory;
  newAbv: string;
}

let keySeq = 0;
const newKey = () => `l${keySeq++}`;
const blankLine = (unit: Unit): Line => ({
  key: newKey(),
  text: '',
  amount: '',
  unit,
  newCategory: 'other',
  newAbv: '',
});

const GLASSES = [
  'Coupe',
  'Nick & Nora',
  'Rocks',
  'Double rocks',
  'Collins',
  'Highball',
  'Martini',
  'Flute',
  'Wine glass',
  'Mug',
  'Tiki mug',
];
const ICE = ['None (up)', 'Large cube', 'Cubed', 'Crushed', 'Pebble', 'Collins spear'];

const matchIngredient = (list: Ingredient[], text: string) =>
  list.find((i) => i.name.toLowerCase() === text.trim().toLowerCase());

export function EditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const riffOf = params.get('riff');
  const inPlaceId = params.get('inplace');
  const fromNumber = params.get('from');
  const isNewDrink = !id;
  const draft = (useLocation().state as { draft?: AiDraft } | null)?.draft ?? null;
  const sourceId = id ?? riffOf ?? null;

  const [ingredients, reloadIngredients] = useAsync(() => api.listIngredients());
  const [settings] = useAsync(api.getSettings);
  const [source, reloadSource] = useAsync<CocktailDetail | null>(
    () => (sourceId ? api.getCocktail(sourceId) : Promise.resolve(null)),
    [sourceId],
  );

  if (ingredients.status === 'error' || source.status === 'error' || settings.status === 'error') {
    const message =
      (ingredients.status === 'error' && ingredients.message) ||
      (source.status === 'error' && source.message) ||
      (settings.status === 'error' && settings.message) ||
      '';
    return (
      <div className="page">
        <ErrorMessage
          message={`Couldn't load the editor. ${message}`}
          onRetry={() => {
            reloadIngredients();
            reloadSource();
          }}
        />
      </div>
    );
  }
  if (ingredients.status !== 'ready' || source.status !== 'ready' || settings.status !== 'ready') {
    return (
      <div className="page">
        <Skeleton height={240} />
      </div>
    );
  }

  // Which version seeds the form: explicit in-place target, an older version, else the newest.
  const versions = source.data?.versions ?? [];
  const seed: VersionDetail | undefined =
    versions.find((v) => v.id === inPlaceId) ??
    versions.find((v) => String(v.versionNumber) === fromNumber) ??
    versions[0];

  return (
    <EditorForm
      key={`${sourceId}:${inPlaceId}:${fromNumber}`}
      draft={isNewDrink ? draft : null}
      ingredients={ingredients.data}
      refreshIngredients={reloadIngredients}
      detail={source.data}
      seed={seed}
      mode={isNewDrink ? (riffOf ? 'riff' : 'new') : inPlaceId ? 'inplace' : 'version'}
      defaultUnit={settings.data.defaultUnit}
      settings={settings.data}
    />
  );
}

/** Method text plus where it came from, so the provenance is saved with the drink. */
const draftNotes = (d: AiDraft) =>
  [d.methodNotes, d.adaptation, ...d.sources.map((s) => `Source: ${s.title} ${s.url}`)]
    .filter(Boolean)
    .join('\n\n');

type Mode = 'new' | 'riff' | 'version' | 'inplace';

function EditorForm({
  ingredients,
  refreshIngredients,
  detail,
  seed,
  mode,
  defaultUnit,
  settings,
  draft,
}: {
  draft: AiDraft | null;
  ingredients: Ingredient[];
  refreshIngredients: () => void;
  detail: CocktailDetail | null;
  seed: VersionDetail | undefined;
  mode: Mode;
  defaultUnit: 'oz' | 'ml';
  settings: Settings;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState(
    mode === 'riff' && detail
      ? `${detail.cocktail.name} riff`
      : mode === 'new'
        ? (draft?.name ?? '')
        : (detail?.cocktail.name ?? ''),
  );
  const [status, setStatus] = useState<CocktailStatus>(
    mode === 'new' || mode === 'riff' ? 'idea' : (detail?.cocktail.status ?? 'idea'),
  );
  const [tags, setTags] = useState(
    mode === 'new' ? (draft?.tags.join(', ') ?? '') : (detail?.cocktail.tags.join(', ') ?? ''),
  );
  const [technique, setTechnique] = useState<Technique>(
    draft?.technique ?? seed?.technique ?? 'shake',
  );
  const [glass, setGlass] = useState(draft?.glass ?? seed?.glass ?? '');
  const [ice, setIce] = useState(draft?.ice ?? seed?.ice ?? '');
  const [garnish, setGarnish] = useState(draft?.garnish ?? seed?.garnish ?? '');
  const [methodNotes, setMethodNotes] = useState(
    draft ? draftNotes(draft) : (seed?.methodNotes ?? ''),
  );
  const [changeNote, setChangeNote] = useState(mode === 'inplace' ? (seed?.changeNote ?? '') : '');
  const [display, setDisplay] = useState<'oz' | 'ml'>(draft ? 'oz' : defaultUnit);
  const [lines, setLines] = useState<Line[]>(() =>
    draft
      ? draft.lines.map((l) => ({
          key: newKey(),
          text: l.ingredient,
          amount: formatAmount(l.amount, l.unit === 'ml' ? 1 : 2),
          unit: l.unit,
          newCategory: l.category ?? 'other',
          newAbv: l.abvPct === undefined ? '' : String(l.abvPct),
        }))
      : seed
        ? seed.lines.map((l) => ({
            key: newKey(),
            text: l.ingredientName,
            amount: formatAmount(l.amount, l.unit === 'ml' ? 1 : 2),
            unit: l.unit,
            newCategory: 'other',
            newAbv: '',
          }))
        : [blankLine(defaultUnit), blankLine(defaultUnit)],
  );
  const [submitted, setSubmitted] = useState<null | 'primary' | 'inplace'>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  // Land where typing is most likely: the name for a new drink, the first amount for a tweak.
  useEffect(() => {
    const target =
      mode === 'new' || mode === 'riff'
        ? formRef.current?.querySelector<HTMLInputElement>('#name')
        : formRef.current?.querySelector<HTMLInputElement>('input[id^="amt-"]');
    target?.focus();
    target?.select(); // typing replaces the current value: the quickest way to change an amount
  }, [mode]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  /** Unit toggle: converts every oz/ml row so the numbers stay the same drink. */
  const switchUnit = (to: 'oz' | 'ml') => {
    setDisplay(to);
    setLines((ls) =>
      ls.map((l) => {
        if (!isVolumeUnit(l.unit) || l.unit === to) return l;
        const n = parseAmount(l.amount);
        return {
          ...l,
          unit: to,
          amount:
            n === null ? l.amount : formatAmount(convertAmount(n, l.unit, to), to === 'ml' ? 1 : 2),
        };
      }),
    );
  };

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    setLines((ls) =>
      arrayMove(
        ls,
        ls.findIndex((l) => l.key === e.active.id),
        ls.findIndex((l) => l.key === e.over!.id),
      ),
    );
  };

  const unmatched = useMemo(
    () => lines.filter((l) => l.text.trim() && !matchIngredient(ingredients, l.text)),
    [lines, ingredients],
  );

  // Live estimate while editing, from library ABVs (or the ABV typed for a new ingredient).
  const estimate = useMemo(() => {
    const measured = lines.flatMap((l) => {
      const amount = parseAmount(l.amount);
      if (!l.text.trim() || amount === null || amount <= 0) return [];
      const known = matchIngredient(ingredients, l.text);
      const abvPct = known ? known.abvPct : Number(l.newAbv) || 0;
      return [{ amount, unit: l.unit, abvPct }];
    });
    return estimateDrink(measured, technique, settings);
  }, [lines, ingredients, technique, settings]);

  useTitle(
    mode === 'new'
      ? 'New drink'
      : mode === 'riff'
        ? 'New riff'
        : mode === 'inplace'
          ? 'Edit in place'
          : 'Log a tweak',
  );
  const title =
    mode === 'new'
      ? 'New drink'
      : mode === 'riff'
        ? 'New riff'
        : mode === 'inplace'
          ? 'Edit in place'
          : 'Log a tweak';
  const kicker = mode === 'new' || mode === 'riff' ? 'Editor' : (detail?.cocktail.name ?? 'Editor');
  const needsNote = mode === 'version';
  // Tweaking is about amounts: tuck the name/status/tags away so they're one tap, not in the way.
  const collapseDrink = mode === 'version' || mode === 'inplace';

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Give the drink a name.';
    if (needsNote && !changeNote.trim()) e.changeNote = 'Say what changed and why.';
    const filled = lines.filter((l) => l.text.trim() || l.amount.trim());
    if (filled.length === 0) e.lines = 'Add at least one ingredient.';
    lines.forEach((l, i) => {
      if (!l.text.trim() && !l.amount.trim()) return;
      if (!l.text.trim()) e[`lines.${i}.text`] = 'Choose an ingredient.';
      const n = parseAmount(l.amount);
      if (n === null || n <= 0) e[`lines.${i}.amount`] = 'Enter an amount, like 2, 0.75 or 3/4.';
      if (unmatched.includes(l)) {
        const abv = l.newAbv.trim() === '' ? 0 : Number(l.newAbv);
        if (!Number.isFinite(abv) || abv < 0 || abv > 100)
          e[`lines.${i}.abv`] = 'ABV must be 0 to 100.';
      }
    });
    return e;
  };

  // After the first save attempt, errors re-check live so they clear as soon as the field is fixed.
  const errors = ((): Record<string, string> => {
    if (!submitted) return {};
    const e = validate();
    if (submitted === 'inplace') delete e.changeNote; // in-place saves never need a change note
    return e;
  })();

  const save = async (kind: 'primary' | 'inplace') => {
    const e = validate();
    if (kind === 'inplace') delete e.changeNote;
    setSubmitted(kind);
    setFormError('');
    if (Object.keys(e).length) {
      requestAnimationFrame(() =>
        formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
      return;
    }
    setBusy(true);
    try {
      // Create any ingredients that aren't in the library yet (once per distinct name).
      const created = new Map<string, Ingredient>();
      for (const l of lines) {
        const text = l.text.trim();
        if (!text || matchIngredient(ingredients, text) || created.has(text.toLowerCase()))
          continue;
        created.set(
          text.toLowerCase(),
          await api.createIngredient({
            name: text,
            category: l.newCategory,
            abvPct: l.newAbv.trim() === '' ? 0 : Number(l.newAbv),
            notes: '',
          }),
        );
      }
      if (created.size) refreshIngredients();
      const resolve = (text: string) =>
        matchIngredient(ingredients, text) ?? created.get(text.trim().toLowerCase())!;
      const version = {
        technique,
        glass: glass.trim(),
        ice: ice.trim(),
        garnish: garnish.trim(),
        methodNotes: methodNotes.trim(),
        changeNote: changeNote.trim(),
        lines: lines
          .filter((l) => l.text.trim())
          .map((l) => ({
            ingredientId: resolve(l.text).id,
            amount: parseAmount(l.amount)!,
            unit: l.unit,
          })),
      };
      const cocktailInput = { name: name.trim(), status, tags: normalizeTags(tags.split(',')) };

      if (mode === 'new' || mode === 'riff') {
        const d = await api.createCocktail(
          { ...cocktailInput, parentId: mode === 'riff' ? detail?.cocktail.id : null },
          version,
        );
        toast('Drink created');
        navigate(`/drinks/${d.cocktail.id}`, { replace: true });
        return;
      }
      const cocktail = detail!.cocktail;
      const metaChanged =
        cocktailInput.name !== cocktail.name ||
        cocktailInput.status !== cocktail.status ||
        cocktailInput.tags.join(',') !== cocktail.tags.join(',');
      if (metaChanged) await api.updateCocktail(cocktail.id, cocktailInput);
      if (kind === 'inplace' || mode === 'inplace')
        await api.updateVersionInPlace(seed!.id, version);
      else await api.addVersion(cocktail.id, version);
      toast(
        kind === 'inplace' || mode === 'inplace'
          ? 'Version updated'
          : `Saved as version ${(detail!.versions[0]?.versionNumber ?? 0) + 1}`,
      );
      navigate(`/drinks/${cocktail.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Ctrl/Cmd+S saves.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        void saveRef.current('primary');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    void save('primary');
  };

  const backTo = detail ? `/drinks/${detail.cocktail.id}` : '/';
  const canInPlace = mode === 'version' && seed !== undefined && seed.tastingCount === 0;
  const primaryLabel = busy
    ? 'Saving…'
    : mode === 'version'
      ? 'Save as new version'
      : mode === 'inplace'
        ? 'Save changes'
        : 'Create drink';

  return (
    <div className="page">
      <PageHeader
        kicker={kicker}
        title={title}
        sub={
          mode === 'version'
            ? `Starting from version ${seed?.versionNumber}. Your changes are saved as a new version.`
            : mode === 'riff'
              ? `A new drink linked to ${detail?.cocktail.name}.`
              : undefined
        }
      />
      {draft && (
        <div className="msg" role="note">
          <strong>
            {draft.kind === 'established'
              ? 'AI draft of an established recipe, not yet saved.'
              : draft.kind === 'adapted'
                ? 'AI adaptation, not yet saved.'
                : 'New AI creation, untested and not yet saved.'}
          </strong>{' '}
          Check the amounts
          {draft.sources.length ? ' against the sources saved in the method notes' : ''}, fill in
          the ABV of any new ingredient, then save.
          {draft.note ? ` ${draft.note}` : ''}
        </div>
      )}
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <section className="card" aria-labelledby="d-title">
          {collapseDrink ? (
            <details className="mu-more" open={Boolean(errors.name)}>
              <summary id="d-title">
                <span className="mu-drink-summary">
                  {name || 'Untitled'} · {status}
                  {tags.trim() ? ` · ${tags.trim()}` : ''}
                </span>
                <span className="muted">Edit details</span>
              </summary>

              <div className="field">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                  aria-invalid={!!errors.name}
                  aria-describedby="name-err"
                />
                {errors.name && (
                  <div className="field-error" id="name-err">
                    {errors.name}
                  </div>
                )}
              </div>
              <div className="row row-2">
                <div>
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as CocktailStatus)}
                  >
                    {COCKTAIL_STATUSES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="tags">Tags</label>
                  <input
                    id="tags"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="gin, sour, smoky"
                    autoComplete="off"
                  />
                </div>
              </div>
            </details>
          ) : (
            <>
              <h2 className="card-title" id="d-title">
                Drink
              </h2>

              <div className="field">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                  aria-invalid={!!errors.name}
                  aria-describedby="name-err"
                />
                {errors.name && (
                  <div className="field-error" id="name-err">
                    {errors.name}
                  </div>
                )}
              </div>
              <div className="row row-2">
                <div>
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as CocktailStatus)}
                  >
                    {COCKTAIL_STATUSES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="tags">Tags</label>
                  <input
                    id="tags"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="gin, sour, smoky"
                    autoComplete="off"
                  />
                </div>
              </div>
            </>
          )}
        </section>

        <section className="card" aria-labelledby="ing-title">
          <div className="mu-card-head">
            <h2 className="card-title" id="ing-title">
              Ingredients
            </h2>
            <div className="tabs" role="group" aria-label="Units">
              {(['oz', 'ml'] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  className="tab"
                  aria-pressed={display === u}
                  onClick={() => switchUnit(u)}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <datalist id="ingredient-names">
            {ingredients.map((i) => (
              <option key={i.id} value={i.name} />
            ))}
          </datalist>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={lines.map((l) => l.key)} strategy={verticalListSortingStrategy}>
              <ul className="mu-lines">
                {lines.map((l, i) => (
                  <SortableLine
                    key={l.key}
                    line={l}
                    index={i}
                    isNew={unmatched.includes(l)}
                    errors={errors}
                    onChange={(patch) => setLine(l.key, patch)}
                    onRemove={() =>
                      setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          {estimate && (
            <p className="mu-estimate" aria-live="polite">
              <span className="mu-estimate-label">Estimate</span>
              <span className="num">
                {estimateText(estimate.abvPct, estimate.volumeMl, display)}
              </span>
            </p>
          )}
          {errors.lines && (
            <div className="field-error" role="alert">
              {errors.lines}
            </div>
          )}
          <div className="actions">
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setLines((ls) => [...ls, blankLine(display)])}
            >
              <Plus size={14} aria-hidden /> Add ingredient
            </button>
          </div>
        </section>

        <section className="card" aria-labelledby="m-title">
          <h2 className="card-title" id="m-title">
            Method
          </h2>
          <div className="row row-2">
            <div>
              <label htmlFor="technique">Technique</label>
              <select
                id="technique"
                value={technique}
                onChange={(e) => setTechnique(e.target.value as Technique)}
              >
                {TECHNIQUES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="glass">Glass</label>
              <input
                id="glass"
                list="glasses"
                value={glass}
                onChange={(e) => setGlass(e.target.value)}
                autoComplete="off"
              />
              <datalist id="glasses">
                {GLASSES.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>
          </div>
          <div className="row row-2">
            <div>
              <label htmlFor="ice">Ice</label>
              <input
                id="ice"
                list="ice-opts"
                value={ice}
                onChange={(e) => setIce(e.target.value)}
                autoComplete="off"
              />
              <datalist id="ice-opts">
                {ICE.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>
            <div>
              <label htmlFor="garnish">Garnish</label>
              <input
                id="garnish"
                value={garnish}
                onChange={(e) => setGarnish(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="notes">Method notes</label>
            <textarea
              id="notes"
              rows={3}
              value={methodNotes}
              onChange={(e) => setMethodNotes(e.target.value)}
            />
          </div>
        </section>

        {mode !== 'new' && mode !== 'riff' && (
          <section className="card" aria-labelledby="c-title">
            <h2 className="card-title" id="c-title">
              What changed
            </h2>
            <label htmlFor="change">Change note{needsNote ? '' : ' (optional)'}</label>
            <textarea
              id="change"
              rows={2}
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="e.g. Cut the syrup to 0.25 oz, too sweet"
              aria-invalid={!!errors.changeNote}
              aria-describedby="change-err"
            />
            {errors.changeNote && (
              <div className="field-error" id="change-err">
                {errors.changeNote}
              </div>
            )}
          </section>
        )}

        {formError && (
          <div className="msg err" role="alert">
            {formError}
          </div>
        )}
        {unmatched.length > 0 && (
          <div className="msg warn" role="status">
            {unmatched.length === 1 ? '1 ingredient is' : `${unmatched.length} ingredients are`} new
            and will be added to your library.
          </div>
        )}

        <div className="actions mu-sticky-actions">
          <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
            {primaryLabel}
          </button>
          {canInPlace && (
            <button
              className="btn btn-ghost"
              type="button"
              disabled={busy}
              onClick={() => void save('inplace')}
            >
              Save in place
            </button>
          )}
          <Link className="btn btn-ghost mu-cancel" to={backTo}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function SortableLine({
  line,
  index,
  isNew,
  errors,
  onChange,
  onRemove,
}: {
  line: Line;
  index: number;
  isNew: boolean;
  errors: Record<string, string>;
  onChange: (patch: Partial<Line>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: line.key,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const textErr = errors[`lines.${index}.text`] ?? errors[`lines.${index}.ingredientId`];
  const amountErr = errors[`lines.${index}.amount`];
  return (
    <li ref={setNodeRef} style={style} className={`mu-line${isDragging ? ' dragging' : ''}`}>
      <button
        type="button"
        className="iconbtn mu-grip"
        aria-label={`Reorder ingredient ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden />
      </button>
      <div className="mu-line-name">
        <label className="sr-only" htmlFor={`ing-${line.key}`}>
          Ingredient {index + 1}
        </label>
        <input
          id={`ing-${line.key}`}
          list="ingredient-names"
          placeholder="Ingredient"
          value={line.text}
          onChange={(e) => onChange({ text: e.target.value })}
          autoComplete="off"
          aria-invalid={!!textErr}
        />
      </div>
      <div className="mu-line-amt">
        <label className="sr-only" htmlFor={`amt-${line.key}`}>
          Amount {index + 1}
        </label>
        <input
          id={`amt-${line.key}`}
          className="num"
          inputMode="decimal"
          placeholder="Amount"
          value={line.amount}
          onChange={(e) => onChange({ amount: e.target.value })}
          autoComplete="off"
          aria-invalid={!!amountErr}
        />
      </div>
      <div className="mu-line-unit">
        <label className="sr-only" htmlFor={`unit-${line.key}`}>
          Unit {index + 1}
        </label>
        <select
          id={`unit-${line.key}`}
          value={line.unit}
          onChange={(e) => onChange({ unit: e.target.value as Unit })}
        >
          {UNITS.map((u) => (
            <option key={u}>{u}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="iconbtn mu-remove"
        aria-label={`Remove ingredient ${index + 1}`}
        onClick={onRemove}
      >
        <X size={16} aria-hidden />
      </button>
      {(textErr || amountErr) && (
        <div className="field-error mu-line-full" role="alert">
          {textErr ?? amountErr}
        </div>
      )}
      {isNew && (
        <div className="mu-line-full mu-newing">
          <span className="muted">New ingredient:</span>
          <label className="sr-only" htmlFor={`cat-${line.key}`}>
            Category
          </label>
          <select
            id={`cat-${line.key}`}
            value={line.newCategory}
            onChange={(e) => onChange({ newCategory: e.target.value as IngredientCategory })}
          >
            {INGREDIENT_CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <label className="sr-only" htmlFor={`abv-${line.key}`}>
            ABV percent
          </label>
          <input
            id={`abv-${line.key}`}
            className="num"
            inputMode="decimal"
            placeholder="ABV %"
            value={line.newAbv}
            onChange={(e) => onChange({ newAbv: e.target.value })}
            aria-invalid={!!errors[`lines.${index}.abv`]}
          />
        </div>
      )}
    </li>
  );
}
